/**
 * Invoice Generator Component
 * Allows service providers to generate invoices from material tickets
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { jobService, MaterialTicket } from '@/services/job.service';
import { invoiceService, Invoice, InvoiceLineItem } from '@/services/invoice.service';

interface InvoiceGeneratorProps {
  jobId: string;
  onInvoiceGenerated?: () => void;
}

export const InvoiceGenerator: React.FC<InvoiceGeneratorProps> = ({
  jobId,
  onInvoiceGenerated
}) => {
  const navigate = useNavigate();
  const [tickets, setTickets] = useState<MaterialTicket[]>([]);
  const [selectedTicketIds, setSelectedTicketIds] = useState<string[]>([]);
  const [isPartial, setIsPartial] = useState(false);
  const [notes, setNotes] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [isGenerating, setIsGenerating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [generatedInvoice, setGeneratedInvoice] = useState<Invoice | null>(null);
  const [previewLineItems, setPreviewLineItems] = useState<InvoiceLineItem[]>([]);

  const loadMaterialTickets = useCallback(async () => {
    try {
      setIsLoading(true);
      const ticketsData = await jobService.getMaterialTickets(jobId);
      setTickets(ticketsData);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load material tickets');
    } finally {
      setIsLoading(false);
    }
  }, [jobId]);

  const calculatePreview = useCallback(() => {
    // Group tickets by material type
    const materialGroups = new Map<string, {
      quantity: number;
      isDiverted: boolean;
      ticketIds: string[]
    }>();

    const selectedTickets = tickets.filter(t => selectedTicketIds.includes(t.id));

    selectedTickets.forEach(ticket => {
      ticket.materials.forEach(material => {
        const existing = materialGroups.get(material.material_type);
        if (existing) {
          existing.quantity += material.weight_tons;
          existing.isDiverted = existing.isDiverted && material.is_diverted;
          existing.ticketIds.push(ticket.id);
        } else {
          materialGroups.set(material.material_type, {
            quantity: material.weight_tons,
            isDiverted: material.is_diverted,
            ticketIds: [ticket.id],
          });
        }
      });
    });

    // Convert to line items (with placeholder pricing - actual pricing comes from backend)
    const lineItems: InvoiceLineItem[] = Array.from(materialGroups.entries()).map(
      ([materialType, data]) => ({
        material_type: materialType,
        quantity: data.quantity,
        unit_price_cents: 0, // Backend will fill this in
        total_cents: 0, // Backend will calculate
        is_diverted: data.isDiverted,
        ticket_ids: data.ticketIds,
      })
    );

    setPreviewLineItems(lineItems);
  }, [selectedTicketIds, tickets]);

  useEffect(() => {
    loadMaterialTickets();
  }, [loadMaterialTickets]);

  useEffect(() => {
    // Calculate preview line items when selection changes
    if (selectedTicketIds.length > 0) {
      calculatePreview();
    } else {
      setPreviewLineItems([]);
    }
  }, [calculatePreview, selectedTicketIds]);

  const toggleTicketSelection = (ticketId: string) => {
    setSelectedTicketIds(prev =>
      prev.includes(ticketId)
        ? prev.filter(id => id !== ticketId)
        : [...prev, ticketId]
    );
  };

  const selectAllTickets = () => {
    setSelectedTicketIds(tickets.map(t => t.id));
  };

  const deselectAllTickets = () => {
    setSelectedTicketIds([]);
  };

  const handleGenerateInvoice = async () => {
    if (selectedTicketIds.length === 0) {
      setError('Please select at least one material ticket');
      return;
    }

    try {
      setIsGenerating(true);
      setError(null);

      const invoice = await invoiceService.generateInvoice({
        job_id: jobId,
        ticket_ids: selectedTicketIds,
        is_partial: isPartial,
        notes: notes.trim() || undefined,
      });

      setGeneratedInvoice(invoice);
      if (onInvoiceGenerated) {
        onInvoiceGenerated();
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to generate invoice');
    } finally {
      setIsGenerating(false);
    }
  };

  const handleSendInvoice = async () => {
    if (!generatedInvoice) return;

    try {
      setIsGenerating(true);
      await invoiceService.sendInvoice(generatedInvoice.id);
      navigate(`/jobs/${jobId}`, {
        state: { message: 'Invoice sent successfully!', activeTab: 'invoices' }
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to send invoice');
      setIsGenerating(false);
    }
  };

  const formatAmount = (cents: number): string => {
    return `$${(cents / 100).toFixed(2)}`;
  };

  const formatDate = (timestamp: number): string => {
    return new Date(timestamp * 1000).toLocaleDateString();
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mb-4"></div>
          <p className="text-neutral-600">Loading material tickets...</p>
        </div>
      </div>
    );
  }

  // Success state - invoice generated
  if (generatedInvoice) {
    return (
      <div className="max-w-4xl mx-auto">
        <div className="bg-green-50 border border-green-200 rounded-lg p-8 text-center">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <h2 className="text-2xl font-bold text-neutral-900 mb-2">Invoice Generated Successfully!</h2>
          <p className="text-neutral-600 mb-6">
            Invoice #{generatedInvoice.invoice_number || generatedInvoice.id.slice(0, 8)}
          </p>

          {/* Invoice Summary */}
          <div className="bg-white rounded-lg p-6 mb-6 text-left">
            <h3 className="font-semibold text-neutral-900 mb-4">Invoice Summary</h3>
            <div className="space-y-3">
              {generatedInvoice.line_items.map((item, idx) => (
                <div key={idx} className="flex justify-between text-sm">
                  <span className="text-neutral-700">
                    {item.material_type} - {item.quantity.toFixed(2)} tons @ {formatAmount(item.unit_price_cents)}/ton
                  </span>
                  <span className="font-medium text-neutral-900">{formatAmount(item.total_cents)}</span>
                </div>
              ))}
              <div className="border-t pt-3 flex justify-between font-semibold">
                <span>Total</span>
                <span className="text-lg text-primary-600">
                  {formatAmount(generatedInvoice.total_amount_cents)}
                </span>
              </div>
            </div>
          </div>

          <div className="flex gap-3 justify-center">
            <button
              onClick={handleSendInvoice}
              disabled={isGenerating}
              className="btn-primary"
            >
              {isGenerating ? 'Sending...' : 'Send to Applicant'}
            </button>
            <button
              onClick={() => navigate(`/jobs/${jobId}`)}
              className="btn-secondary"
            >
              Back to Job
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-neutral-900 mb-2">Generate Invoice</h1>
        <p className="text-neutral-600">Select material tickets to include in the invoice</p>
      </div>

      {error && (
        <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4 flex items-start">
          <svg className="w-5 h-5 text-red-400 mr-3 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <p className="text-sm text-red-800">{error}</p>
        </div>
      )}

      {tickets.length === 0 ? (
        <div className="card text-center py-12">
          <div className="w-16 h-16 bg-neutral-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-neutral-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
          </div>
          <h3 className="text-lg font-semibold text-neutral-900 mb-2">No Material Tickets Found</h3>
          <p className="text-neutral-600">Add material tickets to the job before generating an invoice.</p>
        </div>
      ) : (
        <>
          {/* Material Tickets Selection */}
          <div className="card mb-6">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-lg font-semibold text-neutral-900">Material Tickets</h2>
              <div className="flex gap-2">
                <button onClick={selectAllTickets} className="text-sm text-primary-600 hover:text-primary-700">
                  Select All
                </button>
                <span className="text-neutral-400">|</span>
                <button onClick={deselectAllTickets} className="text-sm text-primary-600 hover:text-primary-700">
                  Deselect All
                </button>
              </div>
            </div>

            <div className="space-y-2">
              {tickets.map(ticket => (
                <div
                  key={ticket.id}
                  onClick={() => toggleTicketSelection(ticket.id)}
                  className={`p-4 border rounded-lg cursor-pointer transition-colors ${
                    selectedTicketIds.includes(ticket.id)
                      ? 'border-primary-500 bg-primary-50'
                      : 'border-neutral-200 hover:border-neutral-300'
                  }`}
                >
                  <div className="flex items-start gap-3">
                    <input
                      type="checkbox"
                      checked={selectedTicketIds.includes(ticket.id)}
                      onChange={() => {}}
                      className="mt-1 h-4 w-4 text-primary-600 focus:ring-primary-500 border-neutral-300 rounded"
                    />
                    <div className="flex-1">
                      <div className="flex justify-between items-start mb-2">
                        <div>
                          <p className="font-medium text-neutral-900">
                            {ticket.ticket_number || `Ticket ${ticket.id.slice(0, 8)}`}
                          </p>
                          <p className="text-sm text-neutral-600">{formatDate(ticket.ticket_date)} - {ticket.facility_name}</p>
                        </div>
                      </div>
                      <div className="flex flex-wrap gap-2">
                        {ticket.materials.map((material, idx) => (
                          <span key={idx} className="inline-flex items-center px-2 py-1 rounded-md text-xs font-medium bg-neutral-100 text-neutral-700">
                            {material.material_type}: {material.weight_tons.toFixed(2)} tons
                            {material.is_diverted && (
                              <svg className="ml-1 w-3 h-3 text-green-500" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                              </svg>
                            )}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Invoice Preview */}
          {previewLineItems.length > 0 && (
            <div className="card mb-6">
              <h2 className="text-lg font-semibold text-neutral-900 mb-4">Invoice Preview</h2>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-neutral-200">
                      <th className="text-left py-3 px-4 text-sm font-medium text-neutral-700">Material Type</th>
                      <th className="text-right py-3 px-4 text-sm font-medium text-neutral-700">Quantity (tons)</th>
                      <th className="text-right py-3 px-4 text-sm font-medium text-neutral-700">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {previewLineItems.map((item, idx) => (
                      <tr key={idx} className="border-b border-neutral-100">
                        <td className="py-3 px-4 text-sm text-neutral-900">{item.material_type}</td>
                        <td className="py-3 px-4 text-sm text-neutral-900 text-right">{item.quantity.toFixed(2)}</td>
                        <td className="py-3 px-4 text-sm text-right">
                          <span className={`inline-flex px-2 py-1 rounded-full text-xs font-medium ${
                            item.is_diverted ? 'bg-green-100 text-green-700' : 'bg-neutral-100 text-neutral-700'
                          }`}>
                            {item.is_diverted ? 'Diverted' : 'Landfill'}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <p className="mt-4 text-sm text-neutral-600">
                <strong>Note:</strong> Pricing will be calculated from facility pricing tables when the invoice is generated.
              </p>
            </div>
          )}

          {/* Invoice Options */}
          <div className="card mb-6">
            <h2 className="text-lg font-semibold text-neutral-900 mb-4">Invoice Options</h2>

            <div className="space-y-4">
              <div className="flex items-start">
                <input
                  id="is-partial"
                  type="checkbox"
                  checked={isPartial}
                  onChange={(e) => setIsPartial(e.target.checked)}
                  className="mt-1 h-4 w-4 text-primary-600 focus:ring-primary-500 border-neutral-300 rounded"
                />
                <label htmlFor="is-partial" className="ml-3">
                  <span className="block text-sm font-medium text-neutral-900">Partial Invoice</span>
                  <span className="block text-sm text-neutral-600">Check this if more invoices will follow for this job</span>
                </label>
              </div>

              <div>
                <label htmlFor="notes" className="block text-sm font-medium text-neutral-900 mb-2">
                  Notes (Optional)
                </label>
                <textarea
                  id="notes"
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  rows={3}
                  placeholder="Add any notes for the applicant..."
                  className="w-full px-3 py-2 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                />
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="flex justify-between items-center">
            <button
              onClick={() => navigate(`/jobs/${jobId}`)}
              className="btn-secondary"
            >
              Cancel
            </button>
            <button
              onClick={handleGenerateInvoice}
              disabled={selectedTicketIds.length === 0 || isGenerating}
              className="btn-primary"
            >
              {isGenerating ? (
                <>
                  <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white inline" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Generating...
                </>
              ) : (
                'Generate Invoice'
              )}
            </button>
          </div>
        </>
      )}
    </div>
  );
};
